import Handsontable, { CellChange } from 'handsontable';

import { CustomColumnHeader, CustomColumnItem, getNumericDataType, ID, LineItem } from 'types';
import {
  DISCOUNT_COLUMN_KEY,
  EXTENDED_QUANTITY_COLUMN_KEY,
  ITEM_INTEL_COLUMN_KEY,
  NEGOTIATION_STARTING_PRICE_COLUMN_KEY,
  NSB_BENCHMARK_COLUMN_KEY,
  PRICE_COLUMN_KEY,
  QUANTITY_COLUMN_KEY,
  SUGGESTED_PRICE_COLUMN_KEY,
  SUPPLIER_QUANTITY_COLUMN_KEY,
  VALID_LINE_ITEM_COLUMN_IDS,
} from '@components/bid_request/details/constants';
import { LineItemChangeActionPayload } from '@providers/LineItemsProvider/action_types';
import { removeComma } from '@utils/table-validations/utils';

import { customColumnItemUpdates } from './emptyCustomColumnItem';

// Read-only slots still emit change events; these have no persisted value.
const DISPLAY_ONLY_COLUMN_KEYS = new Set<string>([
  DISCOUNT_COLUMN_KEY,
  ITEM_INTEL_COLUMN_KEY,
  NSB_BENCHMARK_COLUMN_KEY,
  SUPPLIER_QUANTITY_COLUMN_KEY,
]);

interface GetLineItemChangesPayloadParams {
  changes: (CellChange | null)[];
  errorState?: Record<number, number[]>;
  headerMap: ID[];
  headers: CustomColumnHeader[];
  hotInstance: Handsontable;
  lineItems: LineItem[];
  lotId: ID;
}

export const getLineItemChangesPayload = ({
  changes,
  errorState,
  headerMap,
  headers,
  hotInstance,
  lineItems,
  lotId,
}: GetLineItemChangesPayloadParams): LineItemChangeActionPayload => {
  const numericColumns = [
    EXTENDED_QUANTITY_COLUMN_KEY,
    NEGOTIATION_STARTING_PRICE_COLUMN_KEY,
    PRICE_COLUMN_KEY,
    QUANTITY_COLUMN_KEY,
    SUGGESTED_PRICE_COLUMN_KEY,
  ];
  const validColumnIds = new Set<string>(VALID_LINE_ITEM_COLUMN_IDS);
  const itemsUpdates: Record<ID, Partial<LineItem>> = {};
  const columnsUpdates: Record<ID, Partial<CustomColumnItem>> = {};

  changes.forEach((change: CellChange | null) => {
    if (change === null || change.length < 4) return;
    const [row, property, oldVal, newVal] = change;

    // Handle validation error recovery
    // When a user clears a required field (e.g., name) and then adds the same value back,
    // Handsontable reports oldVal as the original value instead of the empty intermediate state.
    // This causes the change to be skipped incorrectly. We check if the cell is in error state
    // to allow these "recovery" changes through even when oldVal === newVal.
    let col: number;
    if (typeof property === 'number') {
      //  number - Direct column index
      col = property;
    } else if (typeof property === 'string') {
      // string - Column property name that needs conversion to index
      col = hotInstance.propToCol(property);
    } else {
      // ColumnDataGetterSetterFunction - A function used for complex column definition
      // If property is a function, we can't determine column index easily
      // Skip error state check for this case
      col = -1;
    }

    const isInErrorState = col >= 0 ? errorState?.[row]?.includes(col) : false;

    // Skip identical values ONLY if the cell is not currently showing a validation error
    if ((oldVal === newVal && !isInErrorState) || (typeof oldVal === 'number' && String(oldVal) === String(newVal))) return;

    const field = headerMap[property as number];
    if (DISPLAY_ONLY_COLUMN_KEYS.has(String(field))) return;
    // Column 0 holds the line item id.
    const lineItemId = hotInstance.getDataAtCell(row, 0) as ID;

    if (validColumnIds.has(String(field))) {
      const isNumeric = numericColumns.some((colName: string): boolean => (colName === field));

      itemsUpdates[lineItemId] = itemsUpdates[lineItemId] || {};

      // Suggested Price maps to the LineItem's `suggested_price_override` column. Delete / clear
      // resolves to '0' (a real $0 override that persists; supplier sees $0). Null-clearing the
      // override is the reset button's job — see SuggestedPriceCellRenderer.
      if (field === SUGGESTED_PRICE_COLUMN_KEY) {
        const raw = removeComma(newVal);
        itemsUpdates[lineItemId] = {
          ...itemsUpdates[lineItemId],
          suggested_price_override: raw === '' || raw == null ? '0' : String(raw),
        };
        return;
      }

      itemsUpdates[lineItemId] = {
        ...itemsUpdates[lineItemId],
        [field]: isNumeric ? removeComma(newVal) : newVal,
      };
    } else {
      const headerId = field;
      const lineItem = lineItems?.find(({ id }: LineItem): boolean => (
        id === lineItemId
      )) || {} as LineItem;
      const header = headers?.find(({ id }: CustomColumnHeader): boolean => id === headerId);
      const isNumeric = header?.data_type === getNumericDataType();
      const updates = customColumnItemUpdates({
        displayText: (isNumeric ? removeComma(newVal) : newVal) as string,
        headerId,
        headers,
        lineItem,
      });

      if (!updates?.id) return;
      columnsUpdates[updates.id] = columnsUpdates[updates.id] || {};
      columnsUpdates[updates.id] = { ...columnsUpdates[updates.id], ...updates };
    }
  });

  return {
    customColumnItems: columnsUpdates,
    lineItems: itemsUpdates,
    lotId,
  };
};
